import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import { toEditProfileIdentityError } from '../domain/edit-profile-identity-error';
import type { ProfileIdentityDraft, SavedIdentity } from '../domain/profile-identity-draft.model';
import type { IdentityEditScope } from '../domain/profile-identity-draft.model';
import { validateDraft } from '../domain/profile-identity-draft.validation';
import type { EditProfileIdentityGateway } from '../infrastructure/edit-profile-identity.gateway';
import { submitRequested } from './edit-profile-identity.actions';
import { selectDraft, selectProfileId, selectScope } from './edit-profile-identity.selectors';
import { editProfileIdentityActions } from './edit-profile-identity.slice';

/**
 * WORKER: persist the corrected identity. Validates first (pure domain); on
 * failure it dispatches a `validation` error and stops without touching the
 * gateway. All I/O goes through the injected gateway.
 *
 * The draft is sent trimmed, so submitting " jean " for a collected "jean" still
 * reads as "unchanged" on the backend and clears the edit. The scope picks the
 * write: the reference name (admin) or the licence's own spelling (#703).
 */
function* saveWorker() {
  const profileId = (yield select(selectProfileId)) as string | null;
  if (!profileId) return;
  const draft = (yield select(selectDraft)) as ProfileIdentityDraft;
  const scope = (yield select(selectScope)) as IdentityEditScope;

  const errors = validateDraft(draft, scope);
  if (Object.keys(errors).length > 0) {
    yield put(editProfileIdentityActions.saveFailed({ kind: 'validation', fields: errors }));
    return;
  }

  const gateway: EditProfileIdentityGateway = yield getContext('editProfileIdentityGateway');
  yield put(editProfileIdentityActions.saveStarted());
  try {
    const names = { first_name: draft.firstName.trim(), last_name: draft.lastName.trim() };
    const identity = (
      scope === 'licence'
        ? yield call(gateway.updateLicenceName, profileId, names)
        : yield call(gateway.updateIdentity, profileId, {
            ...names,
            linkedin_url: draft.linkedinUrl.trim(),
          })
    ) as SavedIdentity;
    yield put(editProfileIdentityActions.saveSucceeded(identity));
  } catch (e) {
    yield put(editProfileIdentityActions.saveFailed(toEditProfileIdentityError(e)));
  }
}

/** WATCHERS. */
export function* editProfileIdentitySaga() {
  yield all([takeLatest(submitRequested.type, saveWorker)]);
}
